import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useSiteButtons } from '@/hooks/use-site-buttons';
import { getButtonIcon, readButtonConfig } from '@/lib/blocks/button-links';
import { useBlockFieldDiff } from '@/lib/section-proposal-diff/section-review-context';
import type { PlainTextFieldDiff } from '@/lib/section-proposal-diff/types';
import {
  extractDiffText,
  isPlainTextFieldDiff,
  isRichTextFieldDiff,
} from '@/lib/section-proposal-diff/types';
import { cn } from '@/lib/utils';
import type { PageBlock } from '@/types/block';
import type { ButtonStyle } from '@/types/button';
import { AnnotatedRichText } from './annotations';

const BUTTON_STYLE_TO_COLOR_SCHEME: Record<ButtonStyle, 'primary' | 'secondary'> = {
  primary: 'primary',
  secondary: 'secondary',
};

function ButtonDiffOverlay({
  block,
  fieldDiff,
}: {
  block: PageBlock;
  fieldDiff: PlainTextFieldDiff;
}) {
  const { shape, buttonStyle: siteButtonStyle } = useSiteButtons();
  const btnConfig = readButtonConfig(block.props);
  const { iconDiff } = fieldDiff;

  const { oldText, newText } = extractDiffText(fieldDiff.segments);

  const oldIcon = iconDiff ? getButtonIcon(iconDiff.originalIcon) : getButtonIcon(btnConfig.icon);
  const newIcon = iconDiff ? getButtonIcon(iconDiff.proposedIcon) : getButtonIcon(btnConfig.icon);
  const oldPlacement = iconDiff?.originalPlacement ?? btnConfig.iconPlacement ?? 'before';
  const newPlacement = iconDiff?.proposedPlacement ?? btnConfig.iconPlacement ?? 'before';

  const colorScheme = BUTTON_STYLE_TO_COLOR_SCHEME[btnConfig.style ?? 'primary'] ?? 'primary';
  const buttonClassName = cn(
    'shrink min-w-0 max-w-full min-h-9 h-auto whitespace-normal break-words text-center',
    'pointer-events-none flex-col gap-0.5',
  );

  return (
    <Button
      variant="solid"
      colorScheme={colorScheme}
      shape={shape}
      buttonStyle={siteButtonStyle}
      className={buttonClassName}
    >
      <mark
        className="flex items-center gap-1.5 rounded-sm px-1 text-inherit line-through opacity-80"
        style={{ backgroundColor: 'var(--diff-removed)' }}
      >
        {oldIcon && oldPlacement === 'before' && <Icon icon={oldIcon} className="size-4" />}
        {oldText}
        {oldIcon && oldPlacement === 'after' && <Icon icon={oldIcon} className="size-4" />}
      </mark>
      <mark
        className="flex items-center gap-1.5 rounded-sm px-1 text-inherit"
        style={{ backgroundColor: 'var(--diff-added)' }}
      >
        {newIcon && newPlacement === 'before' && <Icon icon={newIcon} className="size-4" />}
        {newText}
        {newIcon && newPlacement === 'after' && <Icon icon={newIcon} className="size-4" />}
      </mark>
    </Button>
  );
}

export function BlockDiffOverlay({
  block,
  children,
}: {
  block: PageBlock;
  children: React.ReactNode;
}) {
  const fieldDiff = useBlockFieldDiff(block.id);

  if (!fieldDiff?.hasChanges) return <>{children}</>;

  if (block.blockType === 'button' && isPlainTextFieldDiff(fieldDiff)) {
    return <ButtonDiffOverlay block={block} fieldDiff={fieldDiff} />;
  }

  // Text-content blocks that render outside the inline editor (e.g. the tag-style
  // topics/practice-area metadata slots) still get the red/green strikethrough.
  if (isRichTextFieldDiff(fieldDiff)) {
    return <AnnotatedRichText fieldDiff={fieldDiff} />;
  }

  // Future block types: fall through to normal render
  return <>{children}</>;
}
